import React, { useState, useEffect } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  ButtonGroup,
  Input,
  Label,
  FormGroup,
  Alert,
  Spinner
} from "reactstrap"
import { Link } from "react-router-dom"
import { withTranslation } from "react-i18next"

// Import Dashboard Components
import DashboardOverview from "./Components/DashboardOverview"
import StatusAnalytics from "./Components/StatusAnalytics"
import FinancialInsights from "./Components/FinancialInsights"
import VendorPerformance from "./Components/VendorPerformance"
import ProjectAnalytics from "./Components/ProjectAnalytics"
import TimelineAnalytics from "./Components/TimelineAnalytics"
import GoodServiceAnalytics from "./Components/GoodServiceAnalytics"
import APIDebugger from "./Components/APIDebugger"
import FilterPanel from "./Components/FilterPanel"

// Import API functions
import {
  getRFQDashboardOverview,
  getDefaultDateRange,
  getEmptyDateRange,
  formatDateForAPI,
  testRFQDashboardAPI
} from "../../../helpers/rfq_dashboard_api"

// Import CSS
import "./rfq-dashboard.scss"

const RFQDashboard = props => {
  // State management
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState("overview")
  const [refreshing, setRefreshing] = useState(false)
  
  // Filter states
  const [filters, setFilters] = useState({
    ...getEmptyDateRange(),
    project_id: null,
    vendor_id: null,
    status_id: null,
    serviceHeadId: null,
    serviceSubheadId: null
  })

  // Data states
  const [dashboardData, setDashboardData] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)

  // Set page title
  document.title = "RFQ Dashboard | VIP Mall"

  // Load dashboard data
  const loadDashboardData = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true)
      setError(null)

      console.log("Loading dashboard data with filters:", filters)

      // Debug: Check authentication
      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}")
      console.log("Auth user:", authUser ? "Present" : "Missing")
      console.log("Access token:", authUser.access_token ? "Present" : "Missing")

      // Debug: Check API URL
      // console.log("API URL:", "https://vipmalldemobackend.erpca.shop/public/api/rfq-dashboard-overview")
      console.log("API URL:", "https://api.v1malls.com/api/rfq-dashboard-overview")

      // Test basic connectivity first
      try {
        // const testResponse = await fetch("https://vipmalldemobackend.erpca.shop/public/api/rfq-dashboard-test", {
        const testResponse = await fetch("https://api.v1malls.com/api/rfq-dashboard-test", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authUser.access_token}`,
            "Accept": "application/json"
          },
          body: JSON.stringify({ test: true })
        })

        console.log("Test API response status:", testResponse.status)
        const testData = await testResponse.json()
        console.log("Test API response data:", testData)

        if (!testResponse.ok) {
          throw new Error(`API test failed: ${testResponse.status} - ${testData.message || 'Unknown error'}`)
        }
      } catch (testError) {
        console.error("API connectivity test failed:", testError)
        throw new Error(`API connectivity issue: ${testError.message}`)
      }

      // Use the actual API call with detailed logging
      console.log("Calling getRFQDashboardOverview with filters:", filters)

      try {
        const response = await getRFQDashboardOverview(filters)
        console.log("getRFQDashboardOverview response:", response)
        console.log("Response type:", typeof response)
        console.log("Response status:", response?.status)
        console.log("Response data:", response?.data)
        console.log("Response.data.status:", response?.data?.status)

        if (response && response.status) {
          setDashboardData(response.data)
          setLastUpdated(new Date())
          console.log("Dashboard data loaded successfully:", response.data)
        } else {
          console.error("API response indicates failure:", response)
          throw new Error(response?.message || "API returned unsuccessful status")
        }
      } catch (apiError) {
        console.error("getRFQDashboardOverview threw error:", apiError)
        console.error("Error type:", typeof apiError)
        console.error("Error properties:", Object.keys(apiError))
        throw new Error(`API call failed: ${apiError.message}`)
      }
    } catch (err) {
      console.error("Dashboard loading error:", err)
      console.error("Error details:", {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      })
      setError(err.message || "Failed to load dashboard data")

      // Fallback to mock data on error for demonstration
      const mockData = {
        summary: {
          total_rfqs: 0,
          active_rfqs: 0,
          assigned_rfqs: 0,
          completion_rate: 0
        },
        financial_overview: {
          total_budget: 0,
          total_final_amount: 0,
          total_security_deposit: 0,
          total_advance_payment: 0,
          total_remaining_amount: 0,
          total_additional_payment: 0
        },
        payment_summary: [],
        status_distribution: [],
        recent_activities: []
      }
      setDashboardData(mockData)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    console.log("Filter change requested:", newFilters)
    setFilters(prev => {
      const updatedFilters = { ...prev, ...newFilters }
      console.log("Updated filters:", updatedFilters)
      return updatedFilters
    })
  }

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true)
    loadDashboardData(false)
  }

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab)
  }

  // Test API connectivity
  const testAPI = async () => {
    try {
      console.log("Testing API connectivity...")
      const response = await testRFQDashboardAPI(filters)
      console.log("API Test Response:", response)
      alert("API Test Successful! Check console for details.")
    } catch (error) {
      console.error("API Test Failed:", error)
      alert("API Test Failed! Check console for details.")
    }
  }

  // Load data on component mount and filter changes
  useEffect(() => {
    loadDashboardData()
  }, [filters])

  // Tab configuration
  const tabs = [
    { key: "overview", label: "Overview", icon: "bx-home-circle" },
    { key: "status", label: "Status Analytics", icon: "bx-bar-chart-alt-2" },
    { key: "financial", label: "Financial Insights", icon: "bx-money" },
    { key: "vendor", label: "Vendor Performance", icon: "bx-user-check" },
    { key: "project", label: "Project Analytics", icon: "bx-building" },
    { key: "timeline", label: "Timeline Trends", icon: "bx-time-five" },
    { key: "goodservice", label: "Good & Service", icon: "bx-package" }
  ]

  // Render loading state
  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
            <div className="text-center">
              <Spinner color="primary" />
              <div className="mt-3">
                <h5>Loading RFQ Dashboard...</h5>
                <p className="text-muted">Please wait while we fetch the latest data</p>
              </div>
            </div>
          </div>
        </Container>
      </div>
    )
  }

  // Render error state
  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg={12}>
              <Alert color="danger" className="text-center">
                <h4 className="alert-heading">Error Loading Dashboard</h4>
                <p>{error}</p>
                <Button color="primary" onClick={() => loadDashboardData()}>
                  <i className="bx bx-refresh me-1"></i>
                  Try Again
                </Button>
              </Alert>
            </Col>
          </Row>

          {/* API Debugger */}
          <Row>
            <Col lg={12}>
              <APIDebugger />
            </Col>
          </Row>
        </Container>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Page Header */}
          <Row>
            <Col lg={12}>
              <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                  <h4 className="mb-1">RFQ Dashboard</h4>
                  {/* <p className="text-muted mb-0">
                    Comprehensive insights into Request for Quotation management
                    {lastUpdated && (
                      <span className="ms-2">
                        • Last updated: {lastUpdated.toLocaleTimeString()}
                      </span>
                    )}
                  </p> */}
                </div>
                <div className="d-flex gap-2">
                 
                 
                  <Link to="/project-management/rfq-list" className="btn btn-primary btn-sm">
                    <i className="bx bx-list-ul me-1"></i>
                    View All RFQs
                  </Link>
                </div>
              </div>
            </Col>
          </Row>

          {/* Filter Panel */}
          <Row className="mb-4">
            <Col lg={12}>
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                loading={refreshing}
              />
            </Col>
          </Row>

          {/* Navigation Tabs */}
          <Row className="mb-4">
            <Col lg={12}>
              <Card>
                <CardBody className="py-2">
                  <ButtonGroup className="w-100 rfq-dashboard-tabs">
                    {tabs.map(tab => (
                      <Button
                        key={tab.key}
                        color={activeTab === tab.key ? "primary" : "light"}
                        onClick={() => handleTabChange(tab.key)}
                        className="d-flex align-items-center justify-content-center"
                      >
                        <i className={`bx ${tab.icon} me-1`}></i>
                        <span className="d-none d-md-inline">{tab.label}</span>
                      </Button>
                    ))}
                  </ButtonGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Dashboard Content */}
          <div className="rfq-dashboard-content">
            {activeTab === "overview" && (
              <DashboardOverview 
                data={dashboardData} 
                filters={filters}
                onRefresh={handleRefresh}
              />
            )}
            {activeTab === "status" && (
              <StatusAnalytics 
                filters={filters}
                onRefresh={handleRefresh}
              />
            )}
            {activeTab === "financial" && (
              <FinancialInsights 
                filters={filters}
                onRefresh={handleRefresh}
              />
            )}
            {activeTab === "vendor" && (
              <VendorPerformance 
                filters={filters}
                onRefresh={handleRefresh}
              />
            )}
            {activeTab === "project" && (
              <ProjectAnalytics 
                filters={filters}
                onRefresh={handleRefresh}
              />
            )}
            {activeTab === "timeline" && (
              <TimelineAnalytics
                filters={filters}
                onRefresh={handleRefresh}
              />
            )}
            {activeTab === "goodservice" && (
              <GoodServiceAnalytics
                filters={filters}
                onRefresh={handleRefresh}
              />
            )}
          </div>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withTranslation()(RFQDashboard)
